import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import 'flatpickr/dist/themes/light.css';

import { Indicator } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { getActivityList } from '../../../../../slices/thunks';
import SimpleBar from 'simplebar-react';

dayjs.extend(isBetween);

const UpcomingSchedules = ({ id }) => {
    const dispatch = useDispatch();
    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);
    const [preloadedDates, setPreloadedDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(dayjs().toDate());

    useEffect(() => {
        const now = dayjs().utc();
        setStart(now.startOf("month").toISOString());
        setEnd(now.endOf("month").toISOString());
    }, []);

    const selector = createSelector(
        state => state.Activities,
        activities => ({
            data: activities.itemActivityList,
            isLoading: activities.isActivityLoading,
        })
    );

    const { data, isLoading } = useSelector(selector);

    useEffect(() => {
        if (data) {
            setPreloadedDates(data.map(activity => dayjs(activity.start).toDate()));
        }
    }, [data]);

    const dayRenderer = (date) => {
        const isSelected = preloadedDates.some(preloadedDate =>
            dayjs(preloadedDate).isSame(date, "day")
        );

        return (
            <Indicator size={6} color="red" offset={-3} disabled={!isSelected}>
                {date.getDate()}
            </Indicator>
        );
    };

    const setRangeCalendar = (date) => {
        setStart(dayjs(date).startOf("month").toISOString());
        setEnd(dayjs(date).endOf("month").toISOString());
    };

    useEffect(() => {
        if (start && end && id) {
            dispatch(getActivityList({ start, end, establishment_id: id }));
        }
    }, [start, end, id, dispatch]);

    return (
        <Card className='p-0 mb-2'>
            <CardHeader className='p-2 align-items-center d-flex'>
                <h5 className="flex-grow-1 m-0">
                    <i className="ri-run-line align-middle me-1 text-muted text-capitalize"></i>Próximas Actividades
                </h5>
            </CardHeader>
            <CardBody className='pb-0 pt-0'>
                <DatePicker
                    value={selectedDate}
                    onChange={setSelectedDate}
                    renderDay={dayRenderer}
                    onNextMonth={setRangeCalendar}
                    onPreviousMonth={setRangeCalendar}
                    onNextYear={setRangeCalendar}
                    onPreviousYear={setRangeCalendar}
                    maxLevel={'year'}
                />
            </CardBody>
            <CardFooter>
                <SimpleBar autoHide={true} className="simplebar-track-primary" style={{ maxHeight: "430px" }}>
                    {isLoading ? (
                        <h6 className="mb-0 p-0 text-center text-wrap w-100">
                            <span className="fs-11 badge bg-success-subtle text-success text-wrap">
                                Está cargando la información...
                            </span>
                        </h6>
                    ) : data && data.length > 0 ? (
                        <ul className="list-group list-group-flush border-dashed mb-0">
                            {data
                                .filter(activity => {
                                    // 🔹 Mostrar actividades del mes actual en lugar del próximo mes
                                    const currentMonthStart = dayjs().startOf("month");
                                    const currentMonthEnd = dayjs().endOf("month");
                                    const startDate = dayjs(activity.start);
                                    return startDate.isBetween(currentMonthStart, currentMonthEnd, null, "[]");
                                })
                                .map((activity, index) => (
                                    <li className="list-group-item p-2" key={activity.id || index}>
                                        <div className="d-flex">
                                            <div className="flex-shrink-0 avatar-xs">
                                                <span className="avatar-title p-1 rounded-circle"
                                                    style={{ backgroundColor: activity.extendedProps.activity_group.bgcolor }}>
                                                    <i className={`${activity.extendedProps.activity_group.icon} fs-16`}
                                                        style={{ color: activity.extendedProps.activity_group.txcolor }}></i>
                                                </span>
                                            </div>
                                            <div className="flex-grow-1 ms-2">
                                                <p className="text-muted m-0">{activity.title}</p>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="fw-semibold">
                                                        <span className="badge" style={{ backgroundColor: activity.extendedProps.activity_priority.bgcolor, color: activity.extendedProps.activity_priority.txcolor }}>
                                                            <i className={`align-middle ${activity.extendedProps.activity_priority.icon}`}></i> {activity.extendedProps.activity_priority.name.toUpperCase()}
                                                        </span>
                                                    </span>
                                                    <span className="text-muted fst-italic">
                                                        <i className="ri-time-line align-bottom"></i> {dayjs(activity.start).format("DD-MM-YYYY")}
                                                    </span>
                                                </div>
                                            </div>

                                        </div>
                                    </li>
                                ))}
                        </ul>
                    ) : (
                        <h6 className="mb-0 p-0 text-center text-wrap w-100">
                            <span className="fs-11 badge bg-info-subtle text-info text-wrap">
                                No hay información para este periodo.
                            </span>
                        </h6>
                    )}
                </SimpleBar>
            </CardFooter>
        </Card>
    );
};

export default UpcomingSchedules;
